import React, { useEffect, useState } from 'react';
import { RefreshCw, CheckCircle2, Camera, User, Store, Bike, Calendar, Search, ImageOff } from 'lucide-react';
import { adminApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

/**
 * CompletedOrdersPage — dedicated admin view of orders that have been delivered.
 * Shows each order's POD photos, delivery timestamps, driver and vendor.
 * Backed by GET /api/admin/orders?status_f=delivered.
 */
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');
const fileUrl = (id) => (id?.startsWith('http') ? id : `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`);

const CompletedOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.orders({ status_f: 'delivered' });
      setOrders(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load completed orders');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = orders.filter((o) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return [o.id, o.vendor_name, o.address, o.driver?.name, o.driver_id]
      .filter(Boolean).some((x) => String(x).toLowerCase().includes(needle));
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" /> Completed orders
          </h1>
          <p className="text-slate-500 mt-1">Every order that has been marked delivered, with proof-of-delivery photos.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search order, vendor, driver…"
              className="pl-8 h-10 w-64"
              data-testid="completed-search-input"
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="completed-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Delivered" value={orders.length} />
        <StatCard label="Total value" value={`$${orders.reduce((s, o) => s + (o.total || 0), 0).toFixed(2)}`} />
        <StatCard label="With POD photos" value={orders.filter((o) => (o.pod_photos?.length || 0) >= 2).length} />
        <StatCard label="No-photo overrides" value={orders.filter((o) => o.pod_skip_reason).length} accent="text-amber-600" />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="completed-orders-table">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-3">Order</th>
                <th className="text-left font-medium px-4 py-3">Vendor</th>
                <th className="text-left font-medium px-4 py-3">Driver</th>
                <th className="text-left font-medium px-4 py-3">Total</th>
                <th className="text-left font-medium px-4 py-3">Delivered</th>
                <th className="text-left font-medium px-4 py-3">POD</th>
                <th className="text-right font-medium px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="text-center text-slate-500 py-10">No completed orders yet.</td></tr>
              ) : filtered.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50" data-testid={`completed-row-${o.id}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{o.id}</td>
                  <td className="px-4 py-3 text-slate-900">{o.vendor_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{o.driver?.name || o.driver_id || '—'}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">${(o.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(o.delivered_at)}</td>
                  <td className="px-4 py-3">
                    {o.pod_skip_reason ? (
                      <Badge className="bg-amber-50 text-amber-700 border-0" data-testid={`pod-override-${o.id}`}>Override</Badge>
                    ) : (
                      <Badge className="bg-emerald-50 text-emerald-700 border-0">{(o.pod_photos?.length || 0)}/2</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(o)} data-testid={`view-completed-${o.id}`}>
                      <Camera className="h-3.5 w-3.5 mr-1" /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <OrderDetailsDialog order={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
    <div className={`mt-1 text-2xl font-semibold ${accent || 'text-slate-900'}`}>{value}</div>
  </div>
);

export const OrderDetailsDialog = ({ order, onClose }) => (
  <Dialog open={Boolean(order)} onOpenChange={(v) => !v && onClose?.()}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          Delivered order · <span className="font-mono text-sm">{order?.id}</span>
        </DialogTitle>
        <DialogDescription>Full proof-of-delivery record. Photos are stored securely on ShipitNow.</DialogDescription>
      </DialogHeader>
      {order && (
        <div className="space-y-4" data-testid="completed-order-details">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Info icon={Store} label="Vendor" value={order.vendor_name || '—'} />
            <Info icon={Bike} label="Driver" value={order.driver?.name || order.driver_id || '—'} />
            <Info icon={User} label="Delivered to" value={order.address || '—'} />
            <Info icon={Calendar} label="Delivered at" value={fmtDate(order.delivered_at)} />
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Items</div>
            <ul className="text-sm space-y-1">
              {(order.items || []).map((it, i) => (
                <li key={`${it.id || it.name}-${i}`} className="flex justify-between">
                  <span className="text-slate-700">{it.qty || 1}× {it.name}</span>
                  <span className="font-medium text-slate-900">${((it.price || 0) * (it.qty || 1)).toFixed(2)}</span>
                </li>
              ))}
              <li className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 font-semibold text-slate-900">
                <span>Total</span><span>${(order.total || 0).toFixed(2)}</span>
              </li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Proof of delivery</div>
            {order.pod_skip_reason ? (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="pod-override-reason">
                <ImageOff className="h-4 w-4 mt-0.5" />
                <div>
                  <div className="font-semibold">No-photo override</div>
                  <div className="mt-0.5">{order.pod_skip_reason}</div>
                </div>
              </div>
            ) : (order.pod_photos || []).length ? (
              <div className="grid grid-cols-2 gap-2" data-testid="pod-photos-grid">
                {order.pod_photos.map((id, i) => (
                  <a key={id || i} href={fileUrl(id)} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 hover:opacity-95">
                    <img src={fileUrl(id)} alt={`POD ${i + 1}`} className="w-full h-full object-cover" data-testid={`pod-photo-${i}`} />
                  </a>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">No photos on file.</div>
            )}
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-none">
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900">{value}</div>
    </div>
  </div>
);

export default CompletedOrdersPage;
